import {
  AttendanceData,
  ComedianWithSpecialsAndSocialMedia,
  FOLLOW_LOCATIONS,
  VenuePagesRow,
  addUserPostalCode,
  followVenue,
  getComedianBySlugOr404,
  getIsEuCountry,
  recordAttendance,
} from "@punchup/data/legacy";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../shadcn/ui/input-otp";
import { NamedError, SentryFeatureTags, logException } from "@punchup/sentry";
import React, { FormHTMLAttributes, useEffect, useRef, useState } from "react";
import { UserEvents, identifyUser, trackEvent } from "@punchup/analytics";
import {
  followComedianById,
  syncContactInfoToCourier,
} from "@punchup/data/client";

import { Database } from "@punchup/data/supabase.types";
import { Loader } from "../common/icons";
import {
  NEW_ACCOUNT_THRESHOLD_SECONDS,
  buildPostAuthUrl,
} from "@punchup/utils";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import dayjs from "@punchup/utils/dayjs";
import { formatIncompletePhoneNumber } from "libphonenumber-js/max";
import { setUser } from "@sentry/nextjs";
import { updateSmsOptIn } from "@punchup/data/legacy";
import { useFeatureGate } from "statsig-react";
import { useRouter } from "next/navigation";

// Supabase verifyOtp error codes that indicate an unexpected, actionable problem
// (server fault or project misconfig/abuse) — as opposed to routine expired/invalid
// codes, which are expected user input and should NOT be escalated to Sentry.
const ACTIONABLE_OTP_ERROR_CODES = new Set<string>([
  "otp_disabled", // email/SMS OTP sign-in disabled in Supabase project config
  "over_request_rate_limit",
  "over_email_send_rate_limit",
  "over_sms_send_rate_limit",
]);

interface Props {
  email: string;
  phone: string;
  signInMethod: "email" | "phone" | "oauth";
  comedianToFollow?:
    | Pick<ComedianWithSpecialsAndSocialMedia, "id" | "slug">
    | Pick<ComedianWithSpecialsAndSocialMedia, "slug">;
  followLocation?: FOLLOW_LOCATIONS;
  ctaSource?: string;
  pageSection?: string;
  venueToFollow?: Pick<VenuePagesRow, "id" | "slug">;
  redirectTo?: string | null;
  /**
   * When true, brand-new accounts are routed through the suggested-comedians
   * onboarding stepper (`/suggested`) before continuing to `redirectTo`.
   * Returning users always go straight to `redirectTo`. Opt-in so non-fan
   * flows (checkout, creator sign-in, etc.) are unaffected.
   */
  routeNewUserToOnboarding?: boolean;
  resetForm: () => void;
  postalCodeRequired: boolean;
  enteredPostalCode: string | null;
  enteredCountryCode: string | null;
  callback?: () => void;
  isSignIn?: boolean;
  attendanceData?: AttendanceData;
  handleAuthComplete?: () => Promise<void>;
}

export default function SignInCodeForm({
  email,
  phone,
  signInMethod,
  comedianToFollow,
  followLocation,
  ctaSource,
  pageSection,
  venueToFollow,
  redirectTo,
  routeNewUserToOnboarding = false,
  resetForm,
  postalCodeRequired,
  enteredPostalCode,
  enteredCountryCode,
  callback,
  isSignIn = true,
  attendanceData,
  handleAuthComplete,
}: Props) {
  const supabase = createClientComponentClient<Database>({});
  const router = useRouter();
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<NodeJS.Timeout>();
  const submissionTimeoutRef = useRef<NodeJS.Timeout>();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [announcement, setAnnouncement] = useState("");
  const trackSignInEvent = (success: boolean, errorMessage?: string) => {
    if (success) {
      trackEvent(UserEvents.LOGGED_IN, {
        method: signInMethod,
      });
    } else {
      trackEvent(UserEvents.LOGIN_FAILED, {
        method: signInMethod,
        error: errorMessage,
      });
    }
  };

  const { gate: magicLinkFeatureGate } = useFeatureGate(
    "sign_in_with_magic_link"
  );
  const magicLinkFeatureEnabled = magicLinkFeatureGate.value;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }

    // Start the initial timer
    startResendTimer();

    // Cleanup timer on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (submissionTimeoutRef.current) {
        clearTimeout(submissionTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (code.length === 6) {
      // Set loading state immediately to show the loader
      setLoading(true);

      // Clear any existing submission timeout
      if (submissionTimeoutRef.current) {
        clearTimeout(submissionTimeoutRef.current);
      }

      // Add a small delay before submitting
      submissionTimeoutRef.current = setTimeout(() => {
        setAnnouncement("Submitting your verification code...");
        formRef.current?.requestSubmit();
      }, 500); // 500ms delay
    }
  }, [code]);

  const startResendTimer = () => {
    setResendTimer(60);
    setIsResendDisabled(true);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          setIsResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError(null);
    setCode(""); // Clear the code input when resending in case user input old code

    try {
      let resendResult;
      if (isSignIn) {
        // Regular sign-in flow
        resendResult = await supabase.auth.signInWithOtp({
          ...(signInMethod === "email" ? { email } : { phone }),
        });
      } else {
        // Update flow - handle email/phone updates separately
        if (signInMethod === "email") {
          // For email updates, first update the email
          const { error: updateError } = await supabase.auth.updateUser({
            email,
          });

          if (updateError) {
            setError(`Failed to update email: ${updateError.message}`);
            trackSignInEvent(false, updateError.message);
            return;
          }

          // Then request the verification code
          resendResult = await supabase.auth.resend({
            type: "email_change",
            email,
          });
        } else {
          // For phone updates, first update the phone number
          const { error: updateError } = await supabase.auth.updateUser({
            phone,
          });

          if (updateError) {
            setError(`Failed to update phone: ${updateError.message}`);
            trackSignInEvent(false, updateError.message);
            return;
          }

          // Then request the verification code
          resendResult = await supabase.auth.resend({
            type: "phone_change",
            phone,
          });
        }
      }

      const { error } = resendResult;

      if (error) {
        setError(`Failed to resend code: ${error.message}`);
        setAnnouncement(`Failed to resend code: ${error.message}`);
        trackEvent(UserEvents.PASSCODE_RESEND_FAILED, {
          message: error.message,
          method: signInMethod,
        });
      } else {
        setAnnouncement("New code sent successfully");
        trackEvent(UserEvents.PASSCODE_RESENT, {
          method: signInMethod,
        });
        startResendTimer();
      }
    } catch (err) {
      setError("An unexpected error occurred while resending the code");
      setAnnouncement("An unexpected error occurred while resending the code");
      logException(err);
      trackSignInEvent(false, "Unexpected error during resend");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit: FormHTMLAttributes<HTMLFormElement>["action"] =
    async () => {
      setLoading(true);
      setAnnouncement("Verifying your code...");
      trackEvent(UserEvents.PASSCODE_SUBMITTED, {
        method: signInMethod,
      });

      if (code.length !== 6) {
        setError("Code must be 6 digits");
        setAnnouncement("Code must be 6 digits");
        setLoading(false);
        return;
      }

      try {
        // Determine the verification type based on the context
        const verifyResult = await supabase.auth.verifyOtp({
          ...(signInMethod === "email"
            ? { email, type: isSignIn ? "email" : "email_change" }
            : { phone, type: isSignIn ? "sms" : "phone_change" }),
          token: code,
        });

        const { data, error } = verifyResult;

        if (error) {
          // Supabase returns the same combined "Token has expired or is invalid"
          // message for both expired and malformed codes; the precise reason lives on
          // error.code (e.g. "otp_expired") and error.status.
          const otpErrorCode = (error as { code?: string }).code;
          const otpErrorStatus = (error as { status?: number }).status;
          setAnnouncement(`Verification failed: ${error.message}`);
          trackEvent(UserEvents.PASSCODE_FAILED_VERIFICATION, {
            message: error.message,
            method: signInMethod,
            code: otpErrorCode,
            email: signInMethod === "email" ? email : undefined,
            phone: signInMethod === "phone" ? phone : undefined,
          });
          // Only escalate to Sentry for *unexpected*, actionable failures — a Supabase
          // 5xx or a config/abuse error code — never routine expired/invalid codes,
          // which would otherwise drown the error stream in non-bugs.
          const isUnexpectedOtpError =
            (otpErrorStatus ?? 0) >= 500 ||
            (otpErrorCode != null &&
              ACTIONABLE_OTP_ERROR_CODES.has(otpErrorCode));
          if (isUnexpectedOtpError) {
            // Correlate the Sentry event to the identity being verified (there is no
            // authenticated user yet on a failed verify) so it is searchable per-user.
            setUser(signInMethod === "email" ? { email } : { username: phone });
            logException(
              new NamedError("OtpVerificationFailed", error.message),
              {
                level: "warning",
                tags: {
                  feature: SentryFeatureTags.Auth,
                  otpErrorCode: otpErrorCode ?? "unknown",
                },
                extra: {
                  method: signInMethod,
                  isSignIn,
                  otpErrorCode,
                  otpErrorStatus,
                  otpType:
                    signInMethod === "email"
                      ? isSignIn
                        ? "email"
                        : "email_change"
                      : isSignIn
                        ? "sms"
                        : "phone_change",
                },
              }
            );
          }
          setError(error.message);
          trackSignInEvent(false, error.message);
          setLoading(false);
        } else {
          setAnnouncement("Code verified successfully!");
          if (data?.user) {
            identifyUser(data.user.id);
            trackEvent(UserEvents.PASSCODE_SUCCESSFULLY_VERIFIED, {
              method: signInMethod,
            });

            if (signInMethod === "phone" && isSignIn) {
              await updateSmsOptIn(supabase, data.user.id);
            }

            // Handle attendance tracking if attendance data is available
            if (attendanceData?.showId && attendanceData?.venueId) {
              await recordAttendance(
                supabase,
                data.user.id,
                attendanceData.showId,
                attendanceData.comedianId,
                attendanceData.venueId
              );
            }
            // Track successful login for all methods
            trackSignInEvent(true);

            if (isSignIn) {
              const created = dayjs(data.user.created_at).utc();
              if (
                dayjs().utc().diff(created, "seconds") <
                NEW_ACCOUNT_THRESHOLD_SECONDS
              ) {
                syncContactInfoToCourier().catch(logException);
              }
            }

            if (handleAuthComplete) {
              await handleAuthComplete();
              return; // Important: return here to prevent default redirect
            }

            if (comedianToFollow) {
              let comedianId: string;
              if ("id" in comedianToFollow && comedianToFollow.id) {
                comedianId = comedianToFollow.id;
              } else if ("slug" in comedianToFollow && comedianToFollow.slug) {
                const fullComedian = await getComedianBySlugOr404(
                  comedianToFollow.slug
                );
                comedianId = fullComedian.id;
              } else {
                return;
              }
              const location = followLocation || FOLLOW_LOCATIONS.OTP_SIGNIN;
              // Analytics tracking is handled server-side in the v2 API
              await followComedianById(
                comedianId,
                location,
                ctaSource,
                pageSection
              );
            }

            if (venueToFollow) {
              await followVenue(
                supabase,
                venueToFollow,
                followLocation || FOLLOW_LOCATIONS.OTP_SIGNIN
              );
            }

            if (postalCodeRequired) {
              if (enteredPostalCode && enteredCountryCode) {
                addUserPostalCode({
                  supabase,
                  postalCode: enteredPostalCode,
                  countryCode: enteredCountryCode,
                  isFromIP: false,
                  isEu: getIsEuCountry(enteredCountryCode),
                });
              } else {
                logException(
                  new Error(
                    `Failed to save user postal code in Sign in code form: enteredPostalCode is ${enteredPostalCode} and enteredCountryCode is ${enteredCountryCode}`
                  )
                );
              }
            }

            if (redirectTo) {
              setAnnouncement("Verification successful. Redirecting...");
              // The full decision — new users detour through the suggested-
              // comedians stepper, returning users go straight through, the
              // welcome banner is appended, and non-internal destinations
              // fall back to "/" (no open redirect) — lives in the pure,
              // unit-tested buildPostAuthUrl.
              const correctedUrl = buildPostAuthUrl({
                redirectTo,
                accountCreatedAt: data.user.created_at,
                routeNewUserToOnboarding,
              });
              // Re-sync server components with the just-established session
              // before soft-navigating. router.refresh() invalidates the client
              // Router Cache, so the destination (e.g. a follow-gated special) is
              // re-rendered server-side with the live session instead of being
              // served a stale, pre-auth prefetch — which is what was bouncing
              // just-subscribed users to /checkout (the failed renders had no
              // server hit at all). router.push() then lands on that fresh
              // render without a full reload. Residual read-your-writes lag on
              // the access check itself is covered by the viewing-token gate.
              //
              // Cost: one extra RSC refetch of the current route, once per
              // sign-in. Deliberately unconditional — EVERY pre-auth cache
              // entry is stale after auth (library, profile, gated pages),
              // not just guest-list destinations, so scoping the refresh
              // would reintroduce the stale-render class of bug elsewhere.
              router.refresh();
              router.push(correctedUrl);
            }

            setLoading(false);
            callback?.();
          }
        }
      } catch (err) {
        setError("An unexpected error occurred");
        setAnnouncement("An error occurred. Please try again.");
        setLoading(false);
        logException(err);
        trackSignInEvent(false, "Unexpected error during verification");
      }
    };

  const handleCodeChange = (newCode: string) => {
    setCode(newCode);
    if (error) setError(null);
  };

  const getMagicLinkInstructionText = () => {
    return (
      <>
        We sent a sign in link to
        <br />
        <span className="font-semibold">
          {signInMethod === "email"
            ? email
            : formatIncompletePhoneNumber(phone)}
        </span>
        <button
          type="button"
          className="inline underline text-[15px] ml-1"
          onClick={resetForm}
        >
          Change {signInMethod}
        </button>
      </>
    );
  };

  const getInstructionText = () => {
    return (
      <>
        We sent a 6-character code to
        <br />
        <span className="font-semibold">
          {signInMethod === "email"
            ? email
            : formatIncompletePhoneNumber(phone)}
        </span>
        <button
          type="button"
          className="inline underline text-[15px] ml-1"
          onClick={resetForm}
        >
          Change {signInMethod}
        </button>
      </>
    );
  };

  return (
    <div className="py-6 space-y-6 border-black border-opacity-10">
      <div className="flex flex-col">
        <h3 className="text-center mb-4 normal-case font-sans font-bold text-[24px] leading-7">
          Check your {signInMethod}
        </h3>
        <p className="mb-1 text-center items-center text-[16px] text-[#787B7E] font-sans font-normal inline">
          {magicLinkFeatureEnabled
            ? getMagicLinkInstructionText()
            : getInstructionText()}
        </p>
        {magicLinkFeatureEnabled ? (
          <>
            <p className="mt-1 mb-0.5 text-center text-[14px] text-[#787B7E] font-sans font-normal">
              Having trouble using the link?
            </p>
            <p className="mb-2 text-center text-[14px] text-[#787B7E] font-sans font-normal">
              Enter the 6-digit code
            </p>
          </>
        ) : (
          <p className="mt-1 mb-2 text-center text-[16px] text-[#787B7E] font-sans font-normal">
            Enter the code below to sign in.
          </p>
        )}

        {/* Announcement for screen readers */}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {announcement}
        </div>

        <form
          ref={formRef}
          id="auth-code-submit"
          className="flex flex-col items-center"
          action={handleCodeSubmit}
          onSubmit={() => setLoading(true)}
        >
          <label htmlFor="code" className="sr-only">
            Code
          </label>
          <InputOTP
            id="code"
            name="code"
            ref={inputRef}
            required
            aria-required="true"
            minLength={6}
            maxLength={6}
            onChange={handleCodeChange}
            onBlur={() => setError(null)}
            value={code}
            containerClassName="justify-center my-2"
          >
            <InputOTPGroup>
              <InputOTPSlot
                index={0}
                className="font-medium text-[24px] py-6 px-4"
              />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot
                index={1}
                className="font-medium text-[24px] py-6 px-4"
              />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot
                index={2}
                className="font-medium text-[24px] py-6 px-4"
              />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot
                index={3}
                className="font-medium text-[24px] py-6 px-4"
              />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot
                index={4}
                className="font-medium text-[24px] py-6 px-4"
              />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot
                index={5}
                className="font-medium text-[24px] py-6 px-4"
              />
            </InputOTPGroup>
          </InputOTP>
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={code.length < 6 || loading}
            className="items-center w-full mt-2 font-semibold text-white shadow-md btn text-[16px] py-[15px] leading-5 disabled:opacity-50 transition-colors duration-200 ease-in-out"
          >
            {loading ? (
              <Loader />
            ) : (
              <React.Fragment key="button_text">Submit</React.Fragment>
            )}
          </button>
        </form>
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={handleResendCode}
            disabled={isResendDisabled || loading}
            className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResendDisabled
              ? `Resend code in ${resendTimer}s`
              : "Resend code"}
          </button>
        </div>
      </div>
    </div>
  );
}
